import styled, { css } from 'styled-components';
import { CurrencyInput } from 'components/ui/Inputs';
import TextInput from 'components/ui/TextInput';
import { Page, Row } from 'components/shared/Layout';
import Button from 'components/ui/Button';
import StyledSelect from 'components/ui/StyledSelect';
import { colours } from 'themes/default';
import { BREAKPOINTS } from 'constants/breakpoints';

const MOBILE_BREAKPOINT = `${BREAKPOINTS.lg.max}px`;

export const StyledDropdown = styled(StyledSelect)`
  margin: 10px 0 20px 0;
  width: 100%;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    font-size: 14px;
    white-space: normal;

    option {
      white-space: normal;
      word-wrap: break-word;
    }
  }
`;

export const StyledHeading = styled.h1`
  display: inline-block;
  margin-bottom: 0;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    display: block;
    font-size: 24px;
    margin-bottom: 12px;
  }
`;

export const StyledPage = styled(Page)`
  padding: 40px;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    padding: 16px;
  }
`;

export const StyledLabel = styled.label`
  display: block;
  padding-bottom: 8px;
`;

export const StyledPriceLabel = styled(StyledLabel)`
  padding-left: 16px;
`;

export const FormContainer = styled.div`
  background: ${colours.Greyscale.MistGrey};
  border: 1px solid ${colours.Greyscale.SilverGrey};
  margin-bottom: 20px;
  padding: 20px;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    padding: 12px;

    > div[class*='Row'] {
      flex-direction: column !important;
      display: flex !important;

      > div[class*='Column'] {
        width: 100% !important;
        max-width: 100% !important;
        flex: 0 0 100% !important;
        margin-bottom: 16px;

        &:last-child {
          margin-bottom: 0;
        }

        /* Full width text inputs on mobile */
        input[type='text'],
        input[type='email'],
        input[type='tel'] {
          width: 100%;
          font-size: 16px;
          word-break: break-all;
          box-sizing: border-box;
        }
      }
    }
  }
`;

export const ClearButton = styled(Button)`
  display: inline-block;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    display: block;
    width: 100%;
    margin-top: 8px;
  }
`;

export const ErrorMessage = styled.span`
  color: red;
  display: block;
`;

export const StyledCurrencyInput = styled(CurrencyInput)`
  display: flex;
  align-items: center;
  font-size: 16px;
  input {
    font-size: 16px;
    border-radius: 5px;
    background-color: ${colours.Greyscale.White};
    color: ${colours.Shades.BlueShade40};
    border: 1px solid ${colours.Greyscale.SilverGrey};
    padding: 0.6em 1.4em 0.5em 0.8em;
    margin-left: 6px;
    width: 60px;
  }
`;

export const TotalBar = styled.div`
  background: linear-gradient(
    180deg,
    ${colours.Greyscale.SilverGrey} 0%,
    ${colours.Greyscale.SilverGrey} 5%,
    ${colours.Greyscale.White} 5%,
    ${colours.Greyscale.White} 15%,
    ${colours.Shades.TintBlue10} 15%,
    ${colours.Shades.TintBlue10} 100%
  );
  text-align: right;
  text-decoration: underline;
  padding: 10px 18px 6px 18px;
  font-weight: 700;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    font-size: 16px;
    padding: 12px;
    text-align: center;
  }
`;

export const BoldLabel = styled.span`
  font-weight: 700;
  padding-right: 12px;
`;

export const StyledBookingReferenceInput = styled(TextInput)`
  width: 100px;
  margin-left: 6px;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    width: 100%;
    margin-left: 0;
  }
`;

export const StyledPassengerDetails = styled(Row)`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
`;

export const StyledExtraDetail = styled.div`
  display: flex;
  justify-content: space-between;
  padding-bottom: 24px;
  border-bottom: 1px solid ${colours.Greyscale.SilverGrey};

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    flex-direction: column;
    gap: 16px;
    padding-bottom: 16px;
  }
`;

export const StyledStationRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding-top: 24px;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    flex-direction: column;
    gap: 16px;
    padding-top: 16px;

    button {
      width: 100%;
      order: 3;
    }
  }
`;

export const StyledExtraNameField = styled.div`
  flex-grow: 0.25;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    width: 100%;

    select {
      width: 100%;
      font-size: 14px;
      white-space: normal;

      option {
        white-space: normal;
        word-wrap: break-word;
      }
    }
  }
`;

export const StyledPriceField = styled.div`
  flex-grow: 0;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    width: 100%;
  }
`;

export const StyledQuantityField = styled.div`
  flex-grow: 0;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    width: 100%;

    input {
      width: 100%;
    }
  }
`;

export const StyledDateField = styled.div`
  flex-grow: 0;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    width: 100%;

    input {
      width: 100%;
    }
  }
`;

export const StyledStationSelect = styled.div`
  width: 45%;
  label {
    text-transform: none;
  }
  label div {
    color: ${colours.Shades.BlueShade40};
  }
  label > div:first-child {
    margin-top: 8px;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    width: 100%;
  }
`;

export const StyledH2 = styled.h2`
  font-size: 18px;
  font-weight: 700;
`;

export const AssociatedPnrSection = styled.form`
  margin-top: 10px;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    label {
      display: flex;
      flex-direction: column;
      gap: 8px;

      > * {
        margin: 0 !important;
      }
    }
  }
`;

export const ExtraDetailsContainer = styled.div`
  position: relative;

  ${({ isDisabled }) => {
    if (isDisabled) {
      return css`
        opacity: 0.6;

        :before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          z-index: 2;
        }
      `;
    }

    return null;
  }};
`;

export const EnterButton = styled(Button)`
  margin-left: 10px;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    margin-left: 0;
    width: 100%;
  }
`;

export const TotalPriceDisplay = styled.div`
  display: flex;
  align-items: center;
  max-height: 41px;
  height: 100%;
  font-weight: 600;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    justify-content: flex-start;
    font-size: 16px;
  }
`;
